import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { AlertCircle, Delete, X } from 'lucide-react';
import { SMSService } from '../../services/smsService';
import { FirebasePhoneAuthService } from '../../services/firebasePhoneAuth';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { useOrbs } from '../../context/OrbContext';
import { toast } from 'react-hot-toast';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import { loginWithPin } from '../../services/pinAuthService';

interface LoginFormData {
  email?: string;
  phone?: string;
  password: string;
}

export const Login: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, setError: setFormError } = useForm<LoginFormData>();
  const { login, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [phoneVerificationStep, setPhoneVerificationStep] = useState<'phone' | 'code'>('phone');
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // ---- Secret PIN login (staff): triple-tap the logo to reveal the PIN pad ----
  const [showPinPad, setShowPinPad] = useState(false);
  const logoTaps = useRef<number[]>([]);
  const handleLogoTap = () => {
    const now = Date.now();
    logoTaps.current = [...logoTaps.current.filter((t) => now - t < 1500), now];
    if (logoTaps.current.length >= 3) {
      logoTaps.current = [];
      setShowPinPad(true);
    }
  };
  
  // Hide phone login until SMS issues are resolved
  const showPhoneLogin = false;
  
  const { orbColors: blobColors, isLoading: orbColorsLoading } = useOrbs();
  
  // Track color changes to detect reverts
  const [colorHistory, setColorHistory] = useState<string[]>([]);
  
  useEffect(() => {
    const colorSignature = `${blobColors.blob1}-${blobColors.blob2}`;
    setColorHistory(prev => {
      const newHistory = [...prev, `${new Date().toISOString()}: ${colorSignature}`].slice(-10);
      console.log('🎨 COLOR HISTORY:', newHistory);
      return newHistory;
    });
  }, [blobColors]);
  
  // Use colors directly like loading screen (no pre-computed styles object)
  
  // Use dynamic colors from OrbContext
  const actualColors = blobColors;
  
  // ============ LOAD SPLIT SCREEN FROM SEPARATE DOCUMENT ============
  const [splitScreenConfig, setSplitScreenConfig] = useState({
    enabled: false,
    images: [] as string[],
    overlayColor: '#000000',
    overlayOpacity: 0.3,
    imagePosition: 'center'
  });
  const [splitScreenLoaded, setSplitScreenLoaded] = useState(false);

  useEffect(() => {
    const loadSplitScreenConfig = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'settings', 'split_screen_config'));
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(`🖼️ Login: Split screen config loaded:`, data);
          setSplitScreenConfig({
            enabled: data.enabled === true,
            images: Array.isArray(data.images) ? data.images : [],
            overlayColor: data.overlayColor || '#000000',
            overlayOpacity: typeof data.overlayOpacity === 'number' ? data.overlayOpacity : 0.3,
            imagePosition: data.imagePosition || 'center'
          });
        }
        setSplitScreenLoaded(true);
      } catch (error) {
        console.error('🖼️ Login: Error loading split screen config:', error);
        setSplitScreenLoaded(true);
      }
    };
    loadSplitScreenConfig();
  }, []);
  // ============ END SPLIT SCREEN ============

  
  // Default to the bundled EARRINGSLOGIN.png so the desktop login page always
  // has its hero image on the right, even if no split-screen config has been
  // saved in Firestore yet. Any image configured in the settings page will
  // still take precedence.
  const DEFAULT_LOGIN_IMAGE = '/EARRINGSLOGIN.png';

  // Randomly select an image from the array - picks once on component mount
  const randomSplitScreenImage = useMemo(() => {
    // Use separate config if available, fallback to OrbContext
    const images = splitScreenConfig.images.length > 0 
      ? splitScreenConfig.images 
      : actualColors.splitScreenImages;
    
    if (images && images.length > 0) {
      const randomIndex = Math.floor(Math.random() * images.length);
      console.log(`🖼️ Selected random image ${randomIndex + 1} of ${images.length}`);
      return images[randomIndex];
    }
    // Fallback to legacy single image, then to the bundled default.
    return actualColors.splitScreenImageUrl || DEFAULT_LOGIN_IMAGE;
  }, [splitScreenConfig.images, actualColors.splitScreenImages, actualColors.splitScreenImageUrl]);
  
  // Debug logging for orb colors
  useEffect(() => {
    console.log('🎨 LOGIN PAGE LOADED - CHECKING COLORS');
    console.log('🎨 Login: Received orb colors', blobColors, 'Loading:', orbColorsLoading);
    console.log('🎨 Login: Individual colors:', {
      blob1: blobColors.blob1,
      blob2: blobColors.blob2,
      blob3: blobColors.blob3,
      blob4: blobColors.blob4,
      blob5: blobColors.blob5
    });
  }, [blobColors, orbColorsLoading]);

  // Force log on every render with timestamp - DETAILED
  const timestamp = new Date().toISOString();
  console.log(`🎨 [${timestamp}] LOGIN RENDER:`, { 
    blobColors, 
    orbColorsLoading,
    blob1: blobColors.blob1,
    blob2: blobColors.blob2,
    blob3: blobColors.blob3,
    blob4: blobColors.blob4,
    blob5: blobColors.blob5,
    isDefault: blobColors.blob1 === '#22d3ee' && blobColors.blob2 === '#3b82f6'
  });
  
  // Test function to manually check Firestore (for debugging)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).testFirestoreColors = async () => {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../../lib/firebase/config');
          const docRef = doc(db, 'settings', 'login_blob_colors');
          const docSnap = await getDoc(docRef);
          const timestamp = new Date().toISOString();
          console.log(`🧪 [${timestamp}] Manual Firestore test:`, {
            exists: docSnap.exists(),
            data: docSnap.exists() ? docSnap.data() : null
          });
        } catch (error) {
          console.error('🧪 Manual Firestore test failed:', error);
        }
      };

      // Also add a function to force update colors
      (window as any).forceUpdateColors = () => {
        const timestamp = new Date().toISOString();
        console.log(`🧪 [${timestamp}] Force updating colors...`);
        console.log(`🧪 [${timestamp}] Current blobColors:`, blobColors);
        console.log(`🧪 [${timestamp}] Current orbColorsLoading:`, orbColorsLoading);
      };

      // Auto-test Firestore on every color change
      (window as any).testFirestoreColors();
      
      // Add a function to force specific colors (for testing)
      (window as any).forceTestColors = () => {
        console.log('🧪 Forcing test colors...');
        // This won't work because we can't directly set orbColors from here
        // but it will help us understand if the issue is in the context
      };
    }
  }, [blobColors, orbColorsLoading]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setStatusError(null); // Clear any previous status errors
      
      if (loginMethod === 'email') {
        // Email + password login
        await login(data.email!, data.password);
        navigate('/intro');
      } else {
        // Phone verification flow (no password needed)
        if (phoneVerificationStep === 'phone') {
          // Step 1: Send SMS code
          await handlePhoneVerification(data.phone!);
          return; // Don't navigate yet
        } else {
          // Step 2: Verify SMS code (password not needed)
          await handleCodeVerification(verificationCode);
          return; // Navigation handled in handleCodeVerification
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
  
      // Check for status-related errors (these should show below the form)
      if (errorMessage.includes('pending approval')) {
        setStatusError('Your account is pending approval. Please contact an administrator to activate your account.');
      } else if (errorMessage.includes('suspended')) {
        setStatusError('Your account has been suspended. Please contact an administrator for assistance.');
      } else if (errorMessage.includes('deleted')) {
        setStatusError('Your account has been deleted. Please contact an administrator for assistance.');
      } else if (errorMessage.includes('status does not allow login')) {
        setStatusError('Your account status does not allow login. Please contact an administrator.');
      } else if (errorMessage.includes('invalid-credential')) {
        // Clear status error for credential issues
        setStatusError(null);
        setFormError('email', { message: 'Invalid email or password' });
        setFormError('password', { message: 'Invalid email or password' });
      } else if (errorMessage.includes('too-many-requests')) {
        setStatusError(null);
        toast.error('Too many login attempts. Please try again later.');
      } else if (errorMessage.includes('network-request-failed')) {
        setStatusError(null);
        toast.error('Network error. Please check your connection and try again.');
      } else {
        // For other errors, still use toast
        setStatusError(null);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle phone verification step
  const handlePhoneVerification = async (phone: string) => {
    try {
      setLoading(true);
      setStatusError(null);
      
      // Use Firebase Phone Authentication
      const result = await FirebasePhoneAuthService.sendVerificationCode(phone);
      
      if (!result.success) {
        throw new Error('Failed to send verification code');
      }
      
      setPhoneNumber(phone);
      setPhoneVerificationStep('code');
      
      toast.success('Verification code sent to your phone!', {
        duration: 5000,
      });
      
    } catch (err) {
      console.error('Phone verification error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send verification code';
      setStatusError(errorMessage);
      
      // Also show error in toast for visibility
      toast.error(errorMessage, {
        duration: 15000,
      });
      
      // Show domain info for debugging
      const currentDomain = window.location.hostname;
      toast.error(`Current domain: ${currentDomain}`, {
        duration: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle SMS code verification
  const handleCodeVerification = async (code: string) => {
    try {
      setLoading(true);
      setStatusError(null);
      
      // Verify SMS code using Firebase Phone Auth
      const result = await FirebasePhoneAuthService.verifyCode(code);
      
      if (!result.success) {
        throw new Error('Invalid verification code');
      }
      
      // Sign in the user with Firebase phone authentication
      await login(phoneNumber, null, { smsVerified: true, userData: result.user });
      navigate('/intro');
      
    } catch (err) {
      console.error('Code verification error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Invalid verification code';
      setStatusError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset phone verification flow
  const resetPhoneVerification = () => {
    setPhoneVerificationStep('phone');
    setVerificationCode('');
    setPhoneNumber('');
    setStatusError(null);
    // Reset Firebase phone auth state
    FirebasePhoneAuthService.reset();
  };

  // The new layout is always rendered. The image pane is hidden on viewports
  // narrower than `lg` (mobile / tablet) via Tailwind's `hidden lg:block`,
  // so on small screens you get a single-pane form-only view, and on desktop
  // you get the form on the left + image on the right. No fallback layout
  // and no Firestore-config flash on first paint.
  void splitScreenLoaded; // referenced to avoid unused-var warnings

  return (
    <div className="relative min-h-screen overflow-hidden bg-black">
      {/* Single layout: form on the left, image on the right (image hidden
          on mobile so the form takes the full width). */}
      <div className="relative min-h-screen p-4 lg:p-6 bg-black">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-2rem)] lg:min-h-[calc(100vh-3rem)] bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] overflow-hidden">
          {/* Left pane: form */}
          <div className="flex items-center justify-center px-8 sm:px-12 lg:px-20 py-12 bg-white">
              <div className="w-full max-w-sm">
                <img
                  src="/cco-black.png"
                  alt="Chorost & Co."
                  onClick={handleLogoTap}
                  className="mx-auto w-72 mb-10 opacity-90 cursor-pointer select-none"
                  draggable={false}
                />
                <div className="text-center mb-8">
                  <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Login</h1>
                  <p className="text-sm text-gray-500 mt-2">Enter your credentials to get in</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="w-full px-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-100 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 transition-all"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                    {errors.email?.message && (
                      <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className="w-full px-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-100 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 transition-all"
                      {...register('password', { required: 'Password is required' })}
                    />
                    {errors.password?.message && (
                      <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>
                    )}
                  </div>

                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                    />
                    Remember me
                  </label>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 mt-2 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    {loading ? (
                      <motion.div
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mx-auto"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                    ) : 'Login'}
                  </motion.button>

                  <p className="text-center text-xs text-gray-500 pt-2">
                    Not a member?{' '}
                    <Link to="/register" className="font-semibold text-gray-900 hover:underline">
                      Create an account
                    </Link>
                  </p>

                  {statusError && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className="p-3 rounded-xl bg-amber-50 border border-amber-200"
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800 leading-relaxed">{statusError}</p>
                      </div>
                    </motion.div>
                  )}
                </form>
              </div>
            </div>

          {/* Right pane: image (hidden on mobile / shown lg+) */}
          <div className="relative overflow-hidden hidden lg:block">
            <img
              src={randomSplitScreenImage}
              alt="Welcome to the Store Center"
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                objectPosition: splitScreenConfig.imagePosition || actualColors.splitScreenImagePosition || 'center'
              }}
            />
            {/* Soft tagline overlay at bottom */}
            <div className="absolute inset-x-0 bottom-0 pt-32 pb-12 px-12 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
              <p className="text-6xl font-light text-white leading-[1.05] tracking-tight drop-shadow-lg">
                Welcome to the<br />
                <span className="font-semibold">Store Center</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden reCAPTCHA container for phone authentication */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>

      {/* Secret staff PIN pad */}
      <AnimatePresence>
        {showPinPad && (
          <PinPad onClose={() => setShowPinPad(false)} onSuccess={() => navigate('/intro')} />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ------------------------------- PIN pad -------------------------------- */

const PinPad: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (code: string) => {
    setBusy(true);
    setError(null);
    try {
      await loginWithPin(code);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Incorrect PIN.');
      setPin('');
      setBusy(false);
    }
  };

  const press = (d: string) => {
    if (busy) return;
    setError(null);
    setPin((prev) => {
      if (prev.length >= 4) return prev;
      const next = prev + d;
      if (next.length === 4) void submit(next);
      return next;
    });
  };

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.94, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0 }}
        className="w-full max-w-xs bg-white rounded-3xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Staff PIN</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Enter your 4-digit PIN to sign in.</p>

        {/* dots */}
        <div className="flex items-center justify-center gap-3 mb-5 h-6">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={
                'h-3.5 w-3.5 rounded-full border ' +
                (i < pin.length ? 'bg-gray-900 border-gray-900' : 'bg-transparent border-gray-300')
              }
            />
          ))}
        </div>

        {error && <p className="text-center text-xs text-red-500 mb-3">{error}</p>}

        <div className="grid grid-cols-3 gap-2.5">
          {keys.map((k) => (
            <button
              key={k}
              onClick={() => press(k)}
              disabled={busy}
              className="h-14 rounded-2xl bg-gray-100 hover:bg-gray-200 text-xl font-medium text-gray-900 transition-colors disabled:opacity-50"
            >
              {k}
            </button>
          ))}
          <div />
          <button
            onClick={() => press('0')}
            disabled={busy}
            className="h-14 rounded-2xl bg-gray-100 hover:bg-gray-200 text-xl font-medium text-gray-900 transition-colors disabled:opacity-50"
          >
            0
          </button>
          <button
            onClick={() => {
              setError(null);
              setPin((p) => p.slice(0, -1));
            }}
            disabled={busy || pin.length === 0}
            className="h-14 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition-colors disabled:opacity-40"
            aria-label="Delete"
          >
            <Delete className="w-5 h-5" />
          </button>
        </div>

        {busy && (
          <div className="mt-4 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
